import { service } from '@maia/cart';
import { IconAngleLeft, IconEnvelopeAlt, mapper } from '@maia/icons';
import {
  Button,
  ButtonLink,
  cardStyles,
  Divider,
  FeaturedIcon,
  PageHeader,
  SectionWithHeader,
  SpecList,
  TextButton,
  Typography,
} from '@maia/ui';
import { formatCurrency } from '@maia/utils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { isNil } from 'es-toolkit';
import { useMemo } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { twMerge } from 'tailwind-merge';
import { match } from 'ts-pattern';
import * as v from 'valibot';
import { getCartOptions } from '../../../../../../features/-cart_bak/cart/queryOptions';
import { getProductsOptions } from '../../../../../../features/add-insurance/get-products/queryOptions';
import { getQuestionsOptions } from '../../../../../../features/add-insurance/get-questions/queryOptions';
import { parseProductsFromQuery } from '../../../../../../features/add-insurance/utils';
import { getCustomerOptions } from '../../../../../../features/customer/customer/queryOptions';

export const Route = createFileRoute(
  '/_authenticated/add-insurance/$customer/_layout-cart/cart/$cart/_layout-cart/summary',
)({
  component: RouteComponent,
  loaderDeps: ({ search: { products } }) => ({ products }),
  loader: async ({
    context: { queryClient },
    params: { customer: customerGuid, cart: cartGuid },
    deps: { products: allProducts },
  }) => {
    const customer = await queryClient.ensureQueryData(
      getCustomerOptions(customerGuid),
    );
    const productGuids = parseProductsFromQuery(allProducts);
    const questionsPromises = productGuids.map(async (guid) => {
      const questionData = await queryClient.ensureQueryData(
        getQuestionsOptions({
          cartGuid: cartGuid,
          customerGuid,
          productGuid: guid,
        }),
      );
      return {
        productGuid: guid,
        questions: questionData,
      };
    });

    const questions = await Promise.all(questionsPromises);

    const products = await queryClient.ensureQueryData(
      getProductsOptions({
        cartGuid: cartGuid,
        customerGuid: customerGuid,
      }),
    );

    await queryClient.invalidateQueries({
      queryKey: ['cart', cartGuid],
    });

    const cart = await queryClient.ensureQueryData({
      ...getCartOptions(cartGuid),
    });

    return {
      customer,
      products,
      questions,
      cart,
    };
  },
  validateSearch: v.object({
    products: v.string(),
  }),
});

function RouteComponent() {
  const { customer, questions, products, cart } = Route.useLoaderData();
  const { cart: cartGuid, customer: customerGuid } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const router = useRouter();

  const { data: cartData, isLoading: cartLoading } = useQuery({
    ...getCartOptions(cartGuid),
    initialData: cart,
  });

  const totalPrice = useMemo(
    () =>
      products.reduce(
        (acc, product) =>
          acc + (Number(product.totalPrice) ? Number(product.totalPrice) : 0),
        0,
      ),
    [products],
  );

  const totalTax = useMemo(
    () =>
      products.reduce(
        (acc, product) =>
          acc +
          (Number(product.totalTax ?? '0')
            ? (Number(product.totalTax ?? '0') ?? 0)
            : 0),
        0,
      ),
    [products],
  );

  const { mutate } = useMutation({
    mutationFn: (type: Parameters<typeof service.submitCart>[number]['type']) =>
      service.submitCart({
        cartGuid: cartGuid,
        customerGuid: customerGuid,
        type,
      }),
    onSuccess: (_, args) => {
      match(args)
        .with('order', () =>
          navigate({
            to: '/add-insurance/$customer/insurance-send',
            params: {
              customer: customerGuid,
            },
          }),
        )
        .with('quotation', () =>
          navigate({
            to: '/add-insurance/$customer/invoice-send',
            params: {
              customer: customerGuid,
            },
          }),
        )
        .with('special', () =>
          navigate({
            to: '/add-insurance/$customer/special-send',
            params: {
              customer: customerGuid,
            },
          }),
        )
        .exhaustive();
    },
  });

  return (
    <div className="layout-sidebar">
      <div className="area-page-header">
        <TextButton
          className="px-32 pt-32"
          as="button"
          onPress={() => router.history.back()}
          intent="standard"
          size="tiny"
          iconLeft={IconAngleLeft}
        >
          Terug
        </TextButton>
        <PageHeader title="Overzicht ingevulde gegevens" />
      </div>
      <div className="area-page-content content-grid">
        <SectionWithHeader title="Client details" className="col-span-full">
          <div className={twMerge(cardStyles({ variant: 'outlined' }), 'p-20')}>
            <SpecList
              items={[
                {
                  title: 'Initialen',
                  value: customer.consumer.initials,
                  isHalf: true,
                },
                {
                  title: 'Voornaam',
                  value:
                    customer.consumer.firstName ?? customer.consumer.initials,
                  isHalf: true,
                },
                {
                  title: 'Achternaam',
                  value: customer.consumer.lastName,
                  isHalf: true,
                },
                {
                  title: 'E-mail',
                  value: customer.consumer.email,
                  isHalf: true,
                },
                {
                  title: 'Telefoonnummer',
                  value: customer.consumer.phone?.toString() ?? '',
                  isHalf: true,
                },
              ]}
            />
          </div>
        </SectionWithHeader>

        {questions.map(({ questions, productGuid }) => {
          const product = products.find((p) => p.guid === productGuid);

          if (!product?.object?.specs && questions.length === 0) {
            return null;
          }

          return (
            <Fragment key={productGuid}>
              <Typography
                variant="display6"
                weight="semibold"
                className="text-primaryMain mt-40"
              >
                {product?.name}
              </Typography>
              {product?.object?.specs && (
                <SectionWithHeader
                  title="Object details"
                  className="col-span-full"
                  key={product?.guid}
                >
                  <div
                    className={twMerge(
                      cardStyles({ variant: 'outlined' }),
                      'p-20',
                    )}
                  >
                    <SpecList
                      items={product.object.specs.flatMap((spec) => {
                        if (isNil(spec.answer.value) || isNil(spec.description))
                          return [];
                        return {
                          title: spec.description,
                          value: spec.answer.value?.toString() ?? '',
                          isHalf: true,
                        };
                      })}
                    />
                  </div>
                </SectionWithHeader>
              )}

              {questions.length > 0 && (
                <SectionWithHeader
                  title="Premie vragen"
                  className="col-span-full"
                >
                  <div
                    className={twMerge(
                      cardStyles({ variant: 'outlined' }),
                      'p-20',
                    )}
                  >
                    <SpecList
                      items={questions.flatMap((q) => {
                        if (isNil(q.answer.value)) return [];
                        return {
                          title: q.title?.length ? q.title : q.phrasing,
                          value: q.answer.value?.toString(),
                          isHalf: true,
                        };
                      })}
                    />
                  </div>
                </SectionWithHeader>
              )}
            </Fragment>
          );
        })}
      </div>
      <div className="flex flex-col area-page-sidebar">
        <Typography
          variant="display6"
          weight="medium"
          className="text-primaryMain mb-32"
        >
          Afronden
        </Typography>

        <ul className="flex flex-col gap-y-24 mb-12 grow">
          {products.map((product) => (
            <li key={product.guid}>
              <div className="flex items-center gap-x-8 mb-8">
                <FeaturedIcon
                  icon={mapper(product.icon)}
                  variant="light-square"
                  size="sm"
                />
                <Typography variant="body-xs" weight="semibold">
                  {product.name}
                </Typography>
              </div>
              <div>
                <ul className="flex flex-col gap-y-8">
                  {product.addOns
                    ?.filter((addon) => addon.answer.value === 'J')
                    .map((addon) => (
                      <li key={addon.guid} className="flex flex-col gap-y-4">
                        <Typography
                          key={addon.guid}
                          variant="body-xs"
                          weight="medium"
                          className="text-grayscale500"
                        >
                          {addon.phrasing}
                        </Typography>
                        {addon.children?.map((child) => (
                          <Typography
                            key={addon.guid}
                            variant="body-xs"
                            weight="regular"
                            className="text-grayscale400"
                          >
                            {
                              child.options.find(
                                (option) => option.value === child.answer.value,
                              )?.label
                            }
                          </Typography>
                        ))}
                      </li>
                    ))}
                  <div>
                    <Typography
                      variant="body-xs"
                      weight="bold"
                      className="text-grayscale400"
                    >
                      {formatCurrency(product.totalPrice)}
                    </Typography>
                    <Typography
                      variant="body-xs"
                      weight="regular"
                      className="text-grayscale400"
                    >
                      {' '}
                      {customer.billingUnit.label}
                    </Typography>
                  </div>
                </ul>
              </div>
            </li>
          ))}
        </ul>
        <Divider className="-ml-32 -mr-32 mb-12" />
        <div>
          <div className="flex justify-between">
            <Typography variant="body-s" weight="medium">
              Totaal {customer.billingUnit.label}
            </Typography>
            <Typography variant="body-s" weight="medium">
              {formatCurrency(totalPrice)}
            </Typography>
          </div>
          <div className="flex justify-between mb-24">
            <Typography
              variant="body-xs"
              weight="regular"
              className="text-grayscale400"
            >
              Assurantiebelasting
            </Typography>
            <Typography
              variant="body-xs"
              weight="regular"
              className="text-grayscale400"
            >
              {formatCurrency(totalTax)}
            </Typography>
          </div>
          {}
          {!cartLoading && (
            <ul className="space-y-12 mt-auto">
              {!cartData.specialLimit && (
                <li>
                  <Button
                    intent="cta"
                    size="large"
                    className="w-full justify-center"
                    onPress={() => mutate('order')}
                  >
                    Order plaatsen
                  </Button>
                </li>
              )}
              {!cartData.specialLimit && (
                <li>
                  <Button
                    intent="primary"
                    iconLeft={IconEnvelopeAlt}
                    size="large"
                    className="w-full justify-center"
                    onPress={() => mutate('quotation')}
                  >
                    Als offerte versturen
                  </Button>
                </li>
              )}
              <li>
                <ButtonLink
                  intent="tertiary"
                  size="large"
                  className="w-full justify-center"
                  to="/add-insurance/$customer/cart/$cart/special"
                  params={{
                    cart: cartGuid,
                    customer: customerGuid,
                  }}
                  search={search}
                >
                  Maatwerk aanvragen
                </ButtonLink>
              </li>
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
